import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer den Dialog "Neuer Mitarbeiter"
 * (`AddEmployeeComponent`). Wird ueber den `<app-dialog-help-button>` im
 * Dialog-Header gestartet (Pattern analog `AVAILABILITY_ADD_HELP_TOUR` /
 * `TEMPLATES_HELP_TOUR`).
 *
 * Erklaerend (nextCondition 'manual', Spotlight + Voice-Over), bewusst
 * getrennt von der formular-gebundenen `EMPLOYEE_ADD_CONTEXTUAL_TOUR`. Diese
 * Tour gibt den fachlichen UEBERBLICK pro Abschnitt und hebt die jeweils
 * erwaehnten Felder per Voice-Over-Cue hervor.
 *
 * Sektion-Steps nutzen `panelIndex` (DOM-Reihenfolge der
 * <mat-expansion-panel>): der TourCoordinator ruft vor jedem
 * `attachToDialog`-Step `expandDialogAccordions(panelIndex)` und klappt damit
 * GENAU dieses Panel auf. Wichtig, weil der Employee-Dialog ein
 * `<mat-accordion>` OHNE `multi` ist — beim Aufklappen eines Panels schliessen
 * sich die anderen. So ist beim Erzaehlen eines Abschnitts immer der richtige
 * offen und seine Felder (lazy gerendert) im DOM, damit die Cues sie pulsen
 * koennen.
 *
 * Cue-Daten (welches Feld wann pulst) leben im Voice-Over-Manifest unter
 * `employee-add-help` und werden vom generate-voiceover-Script in
 * `src/assets/onboarding/audio/de/employee-add-help/<step>.cues.json`
 * geschrieben; `hasCues` wird zur Laufzeit aus dem Audio-Manifest gesetzt.
 *
 * Sieben Schritte (Quick-Mode):
 *  1. Ueberblick (ganzer Dialog)
 *  2. Persoenliche Informationen  (Cues: Vorname, Nachname)
 *  3. Kontakt & Adresse           (Cue: E-Mail-Adresse)
 *  4. Anstellungsdetails          (Cues: Rolle, Abteilung, Faehigkeiten)
 *  5. Arbeitszeit & Urlaub        (Cues: Arbeitstage, Arbeitsstunden, Urlaubstage)
 *  6. Alle Details anzeigen (Full-Mode: Lohn/SV/DATEV)
 *  7. Speichern (+ "Bitte ergaenzen"-Validierung)
 */
export const EMPLOYEE_ADD_HELP_TOUR: TourConfig = {
  id: TourFeature.EmployeeAddHelp,
  version: 1,
  title: 'EMPLOYEE_ADD_HELP_TOUR_TITLE',
  description: 'EMPLOYEE_ADD_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Mitarbeiterliste navigieren, dann den
  // "Mitarbeiter hinzufuegen"-Toolbar-Button klicken. Anchor sitzt in der
  // Employee-Advanced-Table-Toolbar.
  launchContext: {
    route: '/employee',
    opens: { openerAnchor: AnchorId.AddEmployeeButton, settleMs: 350 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 110,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'EMPLOYEE_ADD_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.AddEmployeeDialog,
          spotlightAnchor: AnchorId.AddEmployeeDialog,
          title: 'TUTORIAL.EMPLOYEE_ADD.WELCOME.TITLE',
          content: 'TUTORIAL.EMPLOYEE_ADD.WELCOME.CONTENT',
          placement: 'bottom',
          // Pflicht: ohne panelIndex faellt der Coordinator in den Legacy-Zweig
          // und klickt ALLE Akkordeon-Header durch (Single-Accordion → endet mit
          // dem letzten Panel offen). panelIndex 0 = no-op, weil Personal beim
          // Dialog-Mount schon offen ist → der Ueberblick bleibt wie geoeffnet.
          panelIndex: 0,
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'personal',
          anchor: AnchorId.EmployeePersonalSection,
          spotlightAnchor: AnchorId.EmployeePersonalSection,
          title: 'TUTORIAL.EMPLOYEE_ADD.PERSONAL.TITLE',
          content: 'TUTORIAL.EMPLOYEE_ADD.PERSONAL.CONTENT',
          placement: 'right',
          panelIndex: 0,
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'contact',
          anchor: AnchorId.EmployeeContactSection,
          spotlightAnchor: AnchorId.EmployeeContactSection,
          title: 'TUTORIAL.EMPLOYEE_ADD.CONTACT.TITLE',
          content: 'TUTORIAL.EMPLOYEE_ADD.CONTACT.CONTENT',
          placement: 'right',
          panelIndex: 1,
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'employment',
          // Kernabschnitt fuer die Schichtplanung.
          anchor: AnchorId.EmployeeEmploymentSection,
          spotlightAnchor: AnchorId.EmployeeEmploymentSection,
          title: 'TUTORIAL.EMPLOYEE_ADD.EMPLOYMENT.TITLE',
          content: 'TUTORIAL.EMPLOYEE_ADD.EMPLOYMENT.CONTENT',
          placement: 'right',
          panelIndex: 2,
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="employee-employment-section"]'),
        },
        {
          id: 'worktime',
          anchor: AnchorId.EmployeeWorktimeSection,
          spotlightAnchor: AnchorId.EmployeeWorktimeSection,
          title: 'TUTORIAL.EMPLOYEE_ADD.WORKTIME.TITLE',
          content: 'TUTORIAL.EMPLOYEE_ADD.WORKTIME.CONTENT',
          placement: 'right',
          panelIndex: 3,
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="employee-worktime-section"]'),
        },
        {
          id: 'full-details',
          // Der Schritt erklaert: "die vier Abschnitte reichen" → Spotlight auf
          // das ganze Form (alle vier Sektionen). Popover sitzt kompakt am
          // "Alle Details anzeigen"-Button. Ein Voice-Cue (Manifest) pulst den
          // Button erst, wenn er im Text erwaehnt wird — so wird NICHT die
          // ganze Zeit der Button markiert, waehrend von den Abschnitten die
          // Rede ist. panelIndex -1 = bewusst KEIN Akkordeon-Eingriff (sonst
          // Legacy-"alle Header klicken").
          anchor: AnchorId.EmployeeFullDetailsButton,
          spotlightAnchor: AnchorId.AddEmployeeForm,
          title: 'TUTORIAL.EMPLOYEE_ADD.FULL_DETAILS.TITLE',
          content: 'TUTORIAL.EMPLOYEE_ADD.FULL_DETAILS.CONTENT',
          // popoverOutsideDialog: Popover sitzt NEBEN dem Dialog (rechts, bei
          // engem Viewport links) statt darüber — sonst verdeckt er genau die
          // vier Abschnitte + den Button, die er erklärt. Spotlight bleibt auf
          // dem Form, der Cue pulst den Button.
          placement: 'right',
          panelIndex: -1,
          dialogContext: {
            attachToDialog: true,
            autoCloseOnDialogClose: true,
            popoverOutsideDialog: true,
          },
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="employee-full-details-button"]'),
        },
        {
          id: 'save',
          anchor: AnchorId.SaveEmployeeButton,
          spotlightAnchor: AnchorId.SaveEmployeeButton,
          title: 'TUTORIAL.EMPLOYEE_ADD.SAVE.TITLE',
          content: 'TUTORIAL.EMPLOYEE_ADD.SAVE.CONTENT',
          placement: 'top',
          // -1 = kein Akkordeon-Eingriff (Footer-Button, Panel-Zustand egal).
          panelIndex: -1,
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
